import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Cpu, Zap, Brain, ImageIcon, Camera } from 'lucide-react';

interface ProviderStats {
  provider: string;
  requests: number;
  tokens_input: number;
  tokens_output: number;
  cost_usd: number;
  cost_eur: number;
}

interface ImageStats {
  requests: number;
  cost_usd: number;
  cost_eur: number;
}

interface FreepikStats {
  requests: number;
  daily: {
    downloads: number;
    downloadLimit: number;
    searches: number;
    searchLimit: number;
  };
  cost_eur: number;
  cost_usd: number;
}

interface UsageStatsData {
  providers: ProviderStats[];
  total: {
    requests: number;
    tokens_input: number;
    tokens_output: number;
    cost_usd: number;
    cost_eur: number;
  };
  images?: ImageStats;
  freepik?: FreepikStats;
  period: string;
}

interface UsageStatsProps {
  theme?: 'light' | 'dark' | 'gradient';
}

const API_BASE = '/api';

const providerIcons: Record<string, React.ReactNode> = {
  openai: <Brain className="w-3 h-3" />,
  perplexity: <Zap className="w-3 h-3" />,
  gemini: <Cpu className="w-3 h-3" />
};

const providerColors: Record<string, string> = {
  openai: 'text-green-600 bg-green-100',
  perplexity: 'text-blue-600 bg-blue-100',
  gemini: 'text-purple-600 bg-purple-100'
};

export const UsageStats: React.FC<UsageStatsProps> = ({ theme = 'light' }) => {
  const [stats, setStats] = useState<UsageStatsData | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_BASE}/usage-stats?period=all`);
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch {
        // Error fetching usage stats - silently ignore
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();

    // Refrescar cada 30 segundos
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded]);

  if (isLoading || !stats) {
    return null;
  }

  const formatCurrency = (amount: number) => {
    return amount.toFixed(2);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const isDark = theme === 'dark' || theme === 'gradient';

  // Calcular el coste total incluyendo imágenes y Freepik
  // Nota: stats.total ya incluye Freepik desde el backend, pero images no
  const imageCostEur = stats.images?.cost_eur || 0;
  const imageCostUsd = stats.images?.cost_usd || 0;
  const grandTotalEur = stats.total.cost_eur + imageCostEur;
  const grandTotalUsd = stats.total.cost_usd + imageCostUsd;

  // Total de peticiones incluyendo Freepik
  const totalRequests = stats.total.requests + (stats.images?.requests || 0) + (stats.freepik?.requests || 0);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Botón compacto en la barra - muestra el total incluyendo imágenes */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`
          flex items-center gap-2 px-3 py-1.5 rounded-full transition-all
          ${isDark
            ? 'bg-white/10 hover:bg-white/20 border border-white/20 text-white'
            : 'bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700'
          }
        `}
      >
        <span className="font-semibold text-sm">
          €{formatCurrency(grandTotalEur)}
        </span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown con detalles */}
      {isExpanded && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-[100] animate-in fade-in slide-in-from-top-2 duration-200">
          {/* Header - Total global */}
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-4 text-white">
            <div className="text-xs opacity-80 mb-1">Gasto Total (APIs + Imágenes)</div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold">€{formatCurrency(grandTotalEur)}</span>
              <span className="text-xs opacity-70">(${formatCurrency(grandTotalUsd)})</span>
            </div>
            <div className="mt-2 flex gap-4 text-xs opacity-80">
              <span>{totalRequests} peticiones</span>
              <span>{formatNumber(stats.total.tokens_input + stats.total.tokens_output)} tokens</span>
            </div>
          </div>

          {/* Desglose por proveedor */}
          <div className="p-3 space-y-2">
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-1">
              Desglose de costes
            </div>

            {/* Proveedores de LLM */}
            {stats.providers.map((provider) => (
              <div
                key={provider.provider}
                className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${providerColors[provider.provider] || 'text-gray-600 bg-gray-100'}`}>
                    {providerIcons[provider.provider] || <Cpu className="w-3.5 h-3.5" />}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-800 capitalize">
                      {provider.provider}
                    </div>
                    <div className="text-[10px] text-gray-400">
                      {provider.requests} peticiones · {formatNumber(provider.tokens_input + provider.tokens_output)} tok
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-gray-900">
                    €{formatCurrency(provider.cost_eur)}
                  </div>
                </div>
              </div>
            ))}

            {/* Imágenes IA - integrado en el mismo desglose */}
            {stats.images && stats.images.requests > 0 && (
              <div className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br from-pink-500 to-purple-600 text-white">
                    <ImageIcon className="w-3.5 h-3.5" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-800">
                      Imágenes IA
                    </div>
                    <div className="text-[10px] text-gray-400">
                      {stats.images.requests} imágenes generadas
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-purple-700">
                    €{formatCurrency(stats.images.cost_eur)}
                  </div>
                </div>
              </div>
            )}

            {/* Freepik - Banco de imágenes */}
            {stats.freepik && stats.freepik.requests > 0 && (
              <div className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br from-cyan-500 to-blue-600 text-white">
                    <Camera className="w-3.5 h-3.5" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-800">
                      Freepik
                    </div>
                    <div className="text-[10px] text-gray-400">
                      {stats.freepik.requests} descargas · {stats.freepik.daily.downloads}/{stats.freepik.daily.downloadLimit} hoy
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-cyan-700">
                    €{formatCurrency(stats.freepik.cost_eur)}
                  </div>
                </div>
              </div>
            )}

            {stats.providers.length === 0 && (!stats.images || stats.images.requests === 0) && (
              <div className="text-center py-4 text-sm text-gray-400">
                Sin datos de uso
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
